import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@centric/ui/select";

import {
  isSupportedLocale,
  PSEUDO_LOCALE,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from "~/config/i18n";
import { useUserPreferences } from "~/features/userPreferences";

interface Props {
  id?: string;
}

interface LocaleEntry {
  native: string;
  lang: string;
}

const LOCALE_NATIVE: Record<SupportedLocale, LocaleEntry> = {
  en: { native: "English", lang: "en" },
  "fr-FR": { native: "Français", lang: "fr" },
  "en-XA": { native: "Pseudo (en-XA)", lang: "en" },
};

export const LanguageSelect = ({ id }: Props) => {
  const { preferences, updatePreference } = useUserPreferences();

  const visibleLocales = SUPPORTED_LOCALES.filter(
    (l) => l !== PSEUDO_LOCALE || import.meta.env.DEV,
  );

  // Select's typed value can be null (deselect path) but we only persist a
  // real locale. `isSupportedLocale` is the runtime tripwire if a future
  // change widens the value type.
  const handleLocaleChange = (value: SupportedLocale | null) => {
    if (value && isSupportedLocale(value)) {
      updatePreference("locale", value);
    }
  };

  // Fall back to the locale code if no native-name entry exists (e.g. after
  // adding a locale to preferences but before adding it to `LOCALE_NATIVE`).
  const activeNative = LOCALE_NATIVE[preferences.locale]?.native ?? preferences.locale;

  return (
    <Select value={preferences.locale} onValueChange={handleLocaleChange}>
      <SelectTrigger
        id={id}
        className="h-(--density-control-h) w-48 rounded-lg border-0 bg-muted/60 px-2 shadow-none text-xs"
        aria-label="Language"
      >
        <SelectValue>{activeNative}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {visibleLocales.map((locale) => {
          const { native, lang } = LOCALE_NATIVE[locale];
          return (
            <SelectItem key={locale} value={locale} lang={lang}>
              {native}
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
};
